import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAtomValue, getDefaultStore } from 'jotai';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { isAuthenticatedAtom, isRehydratingAtom, authUserMetaAtom } from '@/store/authAtoms';
import { selectedFacilityAtom } from '@/store/facilityAtom';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { useSessionCaps } from '@/hooks/useSessionCaps';
import { useLiteMode } from '@/hooks/useLiteMode';
import { loadValidPersistedScheduleRestriction } from '@/features/scheduling/lib/scheduleRestriction';
import { loadMobileClinicDayPriorContext } from '@/features/scheduling/lib/mobileClinicDayPriorContext';
import {
  captureSelfRegPriorContext,
  loadSelfRegSiteGroupEntry,
} from '@/features/scheduling/lib/selfRegPriorContext';
import { ensureSelfRegLinkSiteGroupApplied } from '@/lib/bootstrapSiteGroup';

const store = getDefaultStore();

export default function ProtectedRoute() {
  const isRehydrating = useAtomValue(isRehydratingAtom);
  const isAuth = useAtomValue(isAuthenticatedAtom);
  const remoteAccessCtx = useAtomValue(remoteAccessContextAtom);
  const facility = useAtomValue(selectedFacilityAtom);
  const user = useAtomValue(authUserMetaAtom);
  const { isICodeSession, isGuestSession, caps, canAccess } = useSessionCaps();
  const lite = useLiteMode();
  const location = useLocation();
  const { pathname, search, hash } = location;

  // Last-resort guard: if something outside our control clears authUserMetaAtom while
  // a remote access session is still active, restore it so the page doesn't redirect.
  // Under normal operation this should never fire — clearAuthAndRedirect() returns early
  // for remote access sessions before touching any auth state.
  useEffect(() => {
    if (!isAuth && remoteAccessCtx !== null) {
      console.warn('[ProtectedRoute] authUserMetaAtom cleared during remote access session — restoring from context.');
      store.set(authUserMetaAtom, {
        id:       String(remoteAccessCtx.personKey),
        fullName: `Patient ${remoteAccessCtx.personKey}`,
        roles:    ['remoteAccess'],
      });
    }
  }, [isAuth, remoteAccessCtx]);

  // While the bootstrap silent refresh is in-flight, show a spinner rather than
  // redirecting — the token hasn't been set yet but the cookie may still be valid.
  if (isRehydrating) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuth && remoteAccessCtx !== null) {
    // Auth transiently null during remote access — the useEffect above restores it on
    // the next tick. Keep rendering <Outlet /> so the page tree stays mounted and
    // in-progress form state is not lost.
    return <Outlet />;
  }

  const isSchedulePath = /^\/schedule\/?$/i.test(pathname);

  if (!isAuth) {
    // Gen2 parity: /Schedule was public Self-Reg (SelfRegScheduleWorkflow). PP2 hosts the
    // authenticated/iCode wizard at /schedule, so unauthenticated deep links must not force login.
    if (isSchedulePath) {
      // Only a still-valid clinic-day window may divert into the restricted guest flow.
      const restriction = loadValidPersistedScheduleRestriction();
      if (restriction) {
        return (
          <Navigate
            to="/guest-schedule"
            replace
            state={{
              scheduleRestriction: restriction,
              clinicDayEntry: true,
              allowSignIn: true,
            }}
          />
        );
      }
      return <Navigate to="/self-reg" replace />;
    }

    // Root path: send to the appropriate pre-auth entry point.
    //   No facility → /facility-selection  (first-time or cleared selection)
    //   Has facility → /home               (landing hub for sign-in / other options)
    // Deep link (any other path): skip the hub when a facility is already selected
    // and go straight to /sign-in so the user can authenticate with state.from intact.
    let authEntry: string;
    if (pathname === '/') {
      authEntry = facility ? '/home' : '/facility-selection';
    } else {
      authEntry = facility ? '/sign-in' : '/home';
    }
    return <Navigate to={authEntry} replace state={{ from: { pathname, search, hash } }} />;
  }

  // Gen2 /Schedule is always Self-Reg (ScheduleController), including when OWIN already
  // authenticated. PP2 reused /schedule for iCode + clinic-day-after-sign-in, so only those
  // stay on the protected wizard; a normal full session must divert to /self-reg.
  // Appointments "Schedule New" embeds SchedulingWizard inline and does not use this route.
  // Stash prior SiteGroup + auth so cancel/finish can restore (Walmart Self-Reg interrupt).
  if (isSchedulePath && !isICodeSession && !isGuestSession) {
    const clinicDayRestriction = loadValidPersistedScheduleRestriction();
    if (!clinicDayRestriction) {
      // Parking Lot Mobile exit (D10): Schedule clears the restriction then navigates home.
      // A jotai facility restore can re-render this guard while still on /schedule — diverting
      // to Self-Reg would steal the exit. Keep Outlet until Schedule finishes navigating away.
      if (loadMobileClinicDayPriorContext()) {
        return <Outlet />;
      }
      // Fill auth (and prior facility if bootstrap somehow missed it). Prefer entry key /
      // query for linkSiteGroup — never facility.id (already the Self-Reg group after bootstrap).
      const linkSg =
        new URLSearchParams(search).get('SiteGroup')
        ?? new URLSearchParams(search).get('siteGroup')
        ?? new URLSearchParams(search).get('sitegroup')
        ?? loadSelfRegSiteGroupEntry()
        ?? undefined;
      captureSelfRegPriorContext({
        authMeta: user,
        returnPath: '/appointments',
        linkSiteGroup: linkSg,
      });
      // DO NOT REMOVE — remember/apply link SiteGroup before Navigate strips ?SiteGroup=
      // (prerender + divert race). See lib/bootstrapSiteGroup.ts file header.
      ensureSelfRegLinkSiteGroupApplied();
      return <Navigate to="/self-reg" replace />;
    }
  }

  // iCode sessions are restricted to the routes declared in their capability map.
  if (isICodeSession && caps && !canAccess(pathname)) {
    return <Navigate to={caps.landingRoute} replace />;
  }

  // Guest sessions can only reach /appointments and /help.
  if (isGuestSession && !canAccess(pathname)) {
    return <Navigate to="/appointments" replace />;
  }

  // Full sessions only. Guest and iCode keep their existing route maps.
  if (!isICodeSession && !isGuestSession && lite.enabled && !lite.canAccessPath(pathname)) {
    return <Navigate to={lite.landingPath} replace />;
  }

  return <Outlet />;
}
